/**
 * React Query hooks for reading and writing the user's UI preferences.
 *
 * - `staleTime` of 30s keeps reads cheap.
 * - `refetchOnWindowFocus` lets a second device pick up changes when the
 *   user tabs back in, which is the practical convergence story.
 * - The query is gated on `role` and `user_id` being known so it doesn't
 *   fire before the user is hydrated.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getUiPreferences, putUiPreferences, UiPreferences } from '@/api/uiPreferences';
import { usePermissions } from '@/hooks/usePermissions';
import { RootState } from '@/redux/store';

const STALE_TIME = 30_000;

const EMPTY_PREFS: UiPreferences = { quick_links: [], homepage_path: null };

export function useUiPreferences() {
  const userId = useSelector((state: RootState) => state.user.user_id);
  const role = useSelector((state: RootState) => state.user.role);
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const canReadUiPreferences =
    hasPermission('user_ui_preferences', 'read') || hasPermission('user_ui_preferences', 'list');

  const query = useQuery({
    queryKey: ['ui-preferences', userId],
    queryFn: getUiPreferences,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: true,
    enabled: Boolean(role) && Boolean(userId) && !permissionsLoading && canReadUiPreferences,
  });

  return {
    data: query.data ?? EMPTY_PREFS,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useUpdateUiPreferences() {
  const queryClient = useQueryClient();
  const userId = useSelector((state: RootState) => state.user.user_id);

  return useMutation({
    mutationFn: putUiPreferences,
    onSuccess: (data) => {
      queryClient.setQueryData(['ui-preferences', userId], data);
      queryClient.invalidateQueries({ queryKey: ['ui-preferences'] });
    },
  });
}
